/**
 * TEMPL — Cadence-style Subscribe & Save widget
 * ==============================================
 * Injects the elements Loop does not render, so CSS can do the rest:
 *   1. a "-15%" pill next to the "Subscribe & Save" label
 *   2. the "Subscription includes" benefit card (photo, optional struck price -> FREE)
 *      — one entry per item in CONFIG.gifts, so it scales to more than one gift.
 *      Not collapsible — Cadence always shows it expanded, no chevron.
 *      ONE PANEL (Sep 8 2026): the dot-separated reassurance line is the last
 *      section INSIDE this same card, not a second box beside it — Carson
 *      pasted Cadence's markup and its .pr-sub__excl is nested inside
 *      .pr-sub__panel the same way. Two stacked boxes cost a gap plus two
 *      lots of padding that a single panel doesn't pay, which is what made
 *      the earlier build read as "too spread out." See buildCard() below.
 *      TWO-UP TILES (Sep 28 2026): with more than one gift the list switches
 *      to side-by-side tiles — see the LAYOUT note on buildCard().
 *   3. Loop's own frequency box, positioned last by explicit DOM order in
 *      sync(), not CSS `order` (see that function).
 *
 * Everything else in the Cadence look — option borders, price treatment, the
 * framed frequency dropdown — is pure CSS in templ-subscription-widget.css
 * (plus templ-benefit-tiles.css for the two-up layout), loaded from
 * snippets/templ-subscription-widget.liquid.
 * Keep it that way: CSS survives Loop re-renders for free, injected DOM does not.
 *
 * SEPARATE FROM templ-subscription-gift.js ON PURPOSE.
 * That file owns cart reconciliation and is verified. This file is presentation
 * only and cannot break checkout.
 *
 * PROMO GATING — READ THIS BEFORE CHANGING IT
 * -------------------------------------------
 * Earlier revisions gated the card on <html data-templ-flag-promo>, on the
 * assumption that templ-subscription-gift.js set and cleared that attribute from
 * Shopify's clock. IT DOES NOT. That file is cart reconciliation only and never
 * touches documentElement — its own header says PDP messaging lives elsewhere.
 * With that gate in place the card would have silently never rendered.
 *
 * So the card is controlled by CONFIG.showBenefitCard below: a plain boolean.
 * The free-gift program is PERMANENT (Carson, Sep 8 2026) — there is no promo
 * window and nothing expires this automatically. Only flip it to false if the
 * whole free-gift program is ever paused; see promoIsLive() if a date-driven
 * cutoff is ever needed instead.
 *
 * The "-15%" pill is NOT gated. The subscription discount is permanent and
 * unrelated to whether any gift is currently running.
 *
 * ADDING ANOTHER GIFT LATER
 * --------------------------
 * Add one entry to CONFIG.gifts. The header text ("Subscription includes: N
 * FREE gift(s) and ...") and the list of rows both update automatically — no
 * other code in this file needs to change. Also add the matching variant to
 * the GIFTS list in templ-subscription-gift.js so it actually gets added to
 * the cart; this file only renders the card, it does not touch checkout.
 *
 * ⚠️ A RECURRING GIFT IS NOT ADDED BY templ-subscription-gift.js.
 * That file runs at cart time and therefore only ever affects the FIRST order.
 * Anything this card advertises as arriving on later deliveries has to be put
 * on those orders by a Loop Flow. If the Flow is missing, the PDP promises
 * something the renewal never ships — which is a refund conversation with a
 * subscriber, not a cosmetic bug. See the `kicker` note in CONFIG.gifts.
 *
 * LOOP DOM (verified on buildtempl.com, Aug 16 2026)
 * -------------------------------------------------
 * .loop-widget-purchase-options-container            flex column, gap 10px
 *   .loop-widget-purchase-option[data-loop-widget-selling-plan-group]
 *     .loop-widget-purchase-option-header
 *       .loop-widget-spg-label-discount-wrapper      <- pill goes here
 *         .loop-widget-purchase-option-radio
 *         .loop-widget-purchase-option-label
 *       .loop-widget-purchase-option-price-each-container
 *     .loop-widget-spg-container.expanded            <- card + freq box mount here
 *       .loop-widget-sp-selector-description-wrapper (hidden by CSS)
 *       .loop-widget-sp-selector-wrapper             (the 30/45/60 select)
 *   .loop-widget-purchase-option-onetime
 */

const CONFIG = {
  /* Fallback label only, used in the benefit-card header text. The pill next
     to "Subscribe & Save" itself shows dollars saved, computed from the
     rendered prices at runtime (see computeDiscountLabel) — not this value. */
  discountLabel: '-15%',

  /* One entry per free gift shown on the card. TO ADD ANOTHER GIFT LATER: add
     an entry here — the header's count/plural and the list both update
     automatically, and the list switches to two-up tiles above one entry.
     Keep this in sync with the GIFTS list in templ-subscription-gift.js (that
     file is what actually adds the gift to the cart; this one only renders
     the card).

     Fields:
       image     required — absolute CDN URL
       title     required — shown in bold under the photo
       kicker    optional — Cadence's grey eyebrow ("Delivery 1"). If a kicker
                 says or implies the gift repeats, a LOOP FLOW must put it on
                 the renewal orders; templ-subscription-gift.js cannot, it only
                 runs at cart time. See the warning in the header above.
       wasPrice  optional — struck-through retail price. Omit when the product
                 has no retail price set in Shopify rather than inventing one.
       nowPrice  required — normally 'FREE' */
  gifts: [
    {
      // Carson's dedicated "Shaker Free Gift" upload (Sep 8 2026), swapped in
      // for the retail product photo that was here before.
      image:
        'https://cdn.shopify.com/s/files/1/0908/9948/0861/files/Shaker_Free_Gift.png?v=1788905678&width=256',
      title: 'SHAKER BOTTLE',
      kicker: 'Delivery 1',
      wasPrice: '$24.99',
      nowPrice: 'FREE',
    },
    {
      // Added Sep 28 2026. Product: "Monthly Bible Memory Wristband"
      // (variant 53004230525213). No retail price is set on it, so no
      // wasPrice — see the field notes above.
      //
      // Carson's dedicated gift upload, not the product-carousel photo. Like
      // Shaker_Free_Gift.png it is shot on #FAFAFA, which is exactly the
      // benefit card's own fill — that is what makes the photo read as part
      // of the card instead of a pasted-on tile. Any replacement needs the
      // same backdrop; see the BLENDING note in templ-benefit-tiles.css.
      //
      // ⚠️ THE TITLE HERE IS NOT THE PRODUCT THE CART ADDS. Deliberate
      // (Carson, Sep 29 2026). The tile advertises the ongoing programme —
      // "Monthly Bible Memory Wristband", photographed as three bands, kicker
      // "Every delivery". What templ-subscription-gift.js actually puts in the
      // cart is the WELCOME band (ACC-WBD-NEW-D01), because monthly bands are
      // in short supply and are reserved for renewals and repeat buyers.
      // Shopify Flow adds the monthly band (ACC-BMW) to repeat and renewal
      // orders. So a first-time subscriber sees "Monthly Bible Memory
      // Wristband" here and "Bible Memory Wristband" in their cart — that gap
      // is known and accepted, not a bug to "fix" by syncing the strings.
      image:
        'https://cdn.shopify.com/s/files/1/0908/9948/0861/files/Wristband_Free_Gift.png?v=1790639576&width=256',
      title: 'MONTHLY BIBLE MEMORY WRISTBAND',
      kicker: 'Every delivery',
      nowPrice: 'FREE',
    },
  ],

  // Dot-separated reassurance line — the last section of the benefit card
  // itself (see buildInfoSection / .templ-benefits-footer in the CSS), not a
  // separate box. Wording and order set by Carson Aug 16 — reassurance
  // first, then the standing discount.
  footerItems: ['Cancel anytime, no commitment required', '15% off every order'],

  /* Master switch for the gift card + info box. Permanent program — see
     PROMO GATING above. Nothing flips this for you; it stays true unless the
     whole free-gift program is paused. */
  showBenefitCard: true,
};

/* STYLESHEET LOADING — deliberately NOT done here.
 *
 * An earlier build injected the stylesheet from this file using
 * new URL('./templ-subscription-widget.css', import.meta.url). It loaded, but
 * that URL carries no ?v= hash, so Shopify's CDN pinned the first version
 * forever — a cache-busted fetch on Aug 16 still returned the previous build
 * after the file had been updated. Edits appeared to do nothing.
 *
 * The stylesheets are now loaded from snippets/templ-subscription-widget.liquid
 * via asset_url, which versions the URL automatically. Do not reintroduce the
 * JS injection.
 */

const SUB_OPTION = '.loop-widget-purchase-option[data-loop-widget-selling-plan-group]';
const LABEL_WRAP = '.loop-widget-spg-label-discount-wrapper';
const MOUNT = '.loop-widget-spg-container';
const FREQ_WRAP = '.loop-widget-sp-selector-wrapper'; // Loop's own "Deliver Every" box
const PILL_CLASS = 'templ-pill';
const CARD_CLASS = 'templ-benefits';
const INFO_CLASS = 'templ-benefits-footer'; // reassurance section, nested inside CARD_CLASS (see buildCard)
const TILES_CLASS = 'templ-benefits-list--tiles'; // two-up layout, see templ-benefit-tiles.css

/* PREREQUISITE — two blocks must NOT be present in theme.liquid, just above the
   closing body tag. Confirmed already removed from the draft theme Aug 16:

     1. a style block commented "Subscribe & Save Savings Color", targeting
        .loop-widget-purchase-option-price-container[data-savings]::after
     2. a script block commented "Loop Subscribe & Save — Calculate and display
        savings"

   They are a pair: the script computes the dollar saving and writes it to a
   data-savings attribute, the CSS renders it via content: attr(data-savings).
   The "-15%" pill below replaces both, computed from the rendered prices.

   The script also self-triggers: it observes {attributes: true} across the whole
   body subtree and its callback calls setAttribute, which fires a mutation
   record even when the value is unchanged — a permanent ~10/sec loop on every
   PDP. Deleting it is a performance fix, not just a cosmetic one.

   Earlier builds of this file carried an ID-escalated CSS override and an
   injected frequency label to fight those two blocks. Both workarounds are gone;
   if either symptom returns, the theme.liquid blocks are still present. */

/**
 * Whether the free-gift card + info box should be shown.
 *
 * To make it expire on its own, replace the body with a date check, e.g.:
 *
 *   const END = Date.parse('2026-08-24T06:59:00Z'); // 23:59 PT Aug 23
 *   return CONFIG.showBenefitCard && Date.now() < END;
 *
 * Note that uses the SHOPPER's clock, not Shopify's, so a device with the wrong
 * date sees the wrong thing. For a hard cutoff, drive it from Liquid instead.
 * Not needed today — the current gift program has no end date.
 */
function promoIsLive() {
  return CONFIG.showBenefitCard === true;
}

function el(tag, className, text) {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text != null) node.textContent = text;
  return node;
}

/**
 * Reads a money string as a number. Handles thousands separators — the old
 * theme.liquid script used parseFloat("1,299.00"), which silently returns 1.
 * Returns null rather than NaN so callers must handle failure explicitly.
 */
function parseMoney(text) {
  if (!text) return null;
  const cleaned = text.replace(/[^0-9.,]/g, '').replace(/,/g, '');
  const value = Number.parseFloat(cleaned);
  return Number.isFinite(value) ? value : null;
}

/**
 * Derives the pill from the option's own compare-at and discounted price, so
 * changing the selling plan's discount in Loop updates the badge with no code
 * change. The badge shows the dollars saved ("- Save $11.00"), matching
 * Cadence — not a percentage. Returns null if either price is unreadable.
 */
function computeDiscountLabel(option) {
  const compareAt = parseMoney(option.querySelector('.loop-widget-purchase-option-compare-at-price')?.textContent);
  const price = parseMoney(option.querySelector('.loop-widget-purchase-option-price')?.textContent);
  // No hardcoded fallback here on purpose: the saving is a dollar amount and
  // differs per product ($11.00 GENESIS, $10.00 SALT & LYTE, $18.75 THE WHEY,
  // $7.50 CREATINE), so a static string would be wrong on three of four. If the
  // prices cannot be read, show no pill rather than a wrong number.
  if (compareAt === null || price === null) return null;
  if (compareAt <= 0 || price >= compareAt) return null;

  const saved = compareAt - price;
  return saved >= 0.005 ? `- Save $${saved.toFixed(2)}` : null;
}

function buildPill(label) {
  return el('span', PILL_CLASS, label);
}

/** "Subscription includes: N FREE gift(s) and -15% every order" — count/plural derived from CONFIG.gifts. */
function buildHeaderCopy(giftCount) {
  return {
    prefix: `Subscription includes: ${giftCount}`,
    middle: `${giftCount === 1 ? 'gift' : 'gifts'} and`,
    suffix: 'every order',
  };
}

/** One gift entry: photo, optional kicker, title, optional struck price, FREE. */
function buildGift(gift) {
  const item = el('div', 'templ-benefit-item');

  const img = el('div', 'templ-benefit-image');
  img.style.backgroundImage = `url("${gift.image}")`;
  img.setAttribute('role', 'img');
  img.setAttribute('aria-label', gift.title);

  const content = el('div', 'templ-benefit-content');
  if (gift.kicker) content.append(el('div', 'templ-benefit-kicker', gift.kicker));
  content.append(el('div', 'templ-benefit-title', gift.title));

  // wasPrice is optional — omitted for gifts with no retail price set in
  // Shopify, so the row reads just "FREE" rather than striking out a number
  // that does not exist anywhere else on the site.
  const price = el('div', 'templ-benefit-price');
  if (gift.wasPrice) price.append(el('span', 'templ-benefit-was', gift.wasPrice));
  price.append(el('span', 'templ-benefit-now', gift.nowPrice));
  content.append(price);

  item.append(img, content);
  return item;
}

/**
 * Gift card. Always expanded — Cadence has no collapse/chevron on this.
 *
 * LAYOUT (Sep 28 2026): one gift renders as a single full-width row (photo
 * left, text right) exactly as before — a lone half-width card reads as a
 * layout bug. Two or more switch to side-by-side tiles (photo on top, text
 * under), which is Cadence's own treatment and costs one row of height for
 * both gifts instead of two. The switch is the TILES_CLASS below; all the
 * geometry lives in templ-benefit-tiles.css.
 */
function buildCard() {
  const card = el('div', CARD_CLASS);

  const { prefix, middle, suffix } = buildHeaderCopy(CONFIG.gifts.length);
  const copy = el('div', 'templ-benefits-copy');
  copy.append(
    document.createTextNode(prefix + ' '),
    el('strong', null, 'FREE'),
    document.createTextNode(' ' + middle + ' '),
    el('span', 'templ-benefits-pct', CONFIG.discountLabel),
    document.createTextNode(' ' + suffix)
  );

  const header = el('div', 'templ-benefits-header');
  header.append(copy);

  const list = el('div', 'templ-benefits-list');
  if (CONFIG.gifts.length > 1) list.classList.add(TILES_CLASS);
  CONFIG.gifts.forEach((gift) => list.append(buildGift(gift)));

  // Reassurance line, as the card's own last section — not a second box.
  // See the ONE PANEL note at the top of this file and the CSS comment on
  // .templ-benefits-footer for why this changed Sep 8 2026.
  card.append(header, list, buildInfoSection());
  return card;
}

/** Reassurance line (dot-separated), appended as the LAST child of the card
 *  by buildCard() above — a divided section within one panel, matching
 *  Cadence's .pr-sub__excl nested inside .pr-sub__panel. Not a standalone
 *  box; see the CSS comment on .templ-benefits-footer. */
function buildInfoSection() {
  const box = el('div', INFO_CLASS);
  const wrap = el('div', 'templ-bf-wrap');
  CONFIG.footerItems.forEach((textItem, i) => {
    if (i > 0) wrap.append(el('span', 'templ-bf-sep'));
    wrap.append(el('div', 'templ-bf-item', textItem));
  });
  box.append(wrap);
  return box;
}

/** Idempotent. Adds what should exist, removes what shouldn't. */
function sync() {
  const option = document.querySelector(SUB_OPTION);
  if (!option) return;

  // 1. discount pill, derived from the live prices
  const labelWrap = option.querySelector(LABEL_WRAP);
  if (labelWrap) {
    const label = computeDiscountLabel(option);
    const existingPill = labelWrap.querySelector(`.${PILL_CLASS}`);
    if (!label) {
      existingPill?.remove();
    } else if (!existingPill) {
      labelWrap.appendChild(buildPill(label));
    } else if (existingPill.textContent !== label) {
      // textContent only — never setAttribute here. Writing an attribute from
      // inside a MutationObserver callback fires a record even when the value
      // is unchanged, which is how the old theme.liquid script self-triggered
      // into a permanent ~10/sec loop.
      existingPill.textContent = label;
    }
  }

  // 2. shorten the frequency option text so the row reads "Deliver Every: 30 days"
  //    like Cadence's. Only visible text is rewritten — option values are never
  //    touched, so Loop still posts the correct selling plan.
  const select = option.querySelector('select.loop-widget-sp-selector');
  if (select) {
    for (const opt of select.options) {
      const short = opt.textContent.replace(/^\s*deliver\s+every\s*/i, '').trim();
      if (short && short !== opt.textContent) opt.textContent = short;
    }
  }

  // 3. benefit card (which now includes the reassurance line as its own last
  //    section, ONE PANEL — see buildCard()) — gated on CONFIG.showBenefitCard.
  //    Visual order (card, then Loop's own frequency box) is forced by
  //    explicit DOM position every pass, NOT CSS `order`. `order` was tried
  //    first and did not hold — Loop's cross-origin stylesheet loads after
  //    ours and has a documented history of winning equal-specificity ties
  //    on this exact widget (see the PILL comment at the bottom of the CSS
  //    file). Moving nodes is unambiguous in any layout mode and does not
  //    depend on winning a cascade fight.
  const mount = option.querySelector(MOUNT);
  if (!mount) return;
  const existingCard = mount.querySelector(`:scope > .${CARD_CLASS}`);
  const freqWrap = mount.querySelector(FREQ_WRAP);

  if (!promoIsLive()) {
    existingCard?.remove();
    // Loop's own description lines come back when the card is gone.
    option.classList.remove('templ-has-benefits');
    return;
  }
  option.classList.add('templ-has-benefits');

  // appendChild MOVES a node already in the document rather than duplicating
  // it, and is a true no-op (fires no mutation record) once a node is already
  // in the right spot — so this is safe and cheap to re-run every pass: card
  // first, then Loop's frequency box, always last.
  mount.appendChild(existingCard || buildCard());
  if (freqWrap) mount.appendChild(freqWrap);
}

function runSync() {
  try {
    sync();
  } catch (error) {
    // Never let a presentation bug surface to the shopper.
    console.warn('[templ-widget]', error);
  }
}

/**
 * A single rAF-debounced pass is NOT enough.
 *
 * Verified on GENESIS (3 flavours) Aug 16: changing variant makes Loop tear the
 * widget down and rebuild it over multiple frames. One rAF pass runs while that
 * rebuild is still in flight — it injects into DOM that Loop then replaces, and
 * because the replacement produces no further mutation we care about, nothing
 * ever re-runs. Result: pill gone, card gone, option text reverted to Loop's
 * "Deliver every 30 days". Manual sync() recovered it instantly, which is what
 * identified this as a timing race rather than a dead observer.
 *
 * So each mutation burst schedules an immediate pass plus trailing ones.
 * sync() is idempotent, so extra passes cost nothing and settle silently.
 *
 * The timers are cleared and reset per burst, so this stays bounded — it cannot
 * become the runaway loop the old theme.liquid script fell into. Note also that
 * we deliberately do NOT observe attributes on the widget: sync() adds a class,
 * which would retrigger the observer forever.
 */
const TRAILING_PASSES_MS = [300, 900, 1800, 3000];
let rafQueued = false;
let trailingTimers = [];

function scheduleSync() {
  if (!rafQueued) {
    rafQueued = true;
    requestAnimationFrame(() => {
      rafQueued = false;
      runSync();
    });
  }
  trailingTimers.forEach(clearTimeout);
  trailingTimers = TRAILING_PASSES_MS.map((ms) => setTimeout(runSync, ms));
}

/**
 * Safety net. Trailing timers alone were NOT reliable: switching to a third
 * flavour on GENESIS wiped the widget again, because when the page is busy the
 * rAF pass gets deferred and the trailing timers all fire while Loop's rebuild
 * is still in flight. After that the page goes quiet and nothing re-runs.
 *
 * Rather than guess a longer delay — any fixed number is wrong on a slow enough
 * device — this re-checks on a slow interval forever. sync() early-returns as
 * soon as it sees the pill and card already present, so the steady-state cost
 * is two querySelectors every two seconds.
 *
 * This is emphatically NOT the pattern that broke theme.liquid: that observed
 * {attributes: true} across the whole body AND wrote an attribute from inside
 * its own callback, retriggering itself ~10x/sec. This is a fixed 0.5Hz timer
 * that writes nothing when the DOM is already correct.
 */
const SAFETY_INTERVAL_MS = 2000;
setInterval(runSync, SAFETY_INTERVAL_MS);

new MutationObserver(scheduleSync).observe(document.body, { childList: true, subtree: true });

// Variant swatches and the frequency select both surface as change events.
// Capture phase so we see them even if a handler stops propagation.
document.addEventListener('change', scheduleSync, true);

document.addEventListener('DOMContentLoaded', scheduleSync);
scheduleSync();
